import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { FloppyDisk, Warning, Translate } from "@phosphor-icons/react";
import { PageLoading } from "@/components/layout/Layout";
import { useT } from "@/contexts/I18nContext";

export default function Settings() {
    const { t, lang, setLang } = useT();
    const [threshold, setThreshold] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/settings")
            .then((r) => setThreshold(String(r.data.fcr_alert_threshold)))
            .finally(() => setLoading(false));
    }, []);

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.put("/settings", { fcr_alert_threshold: Number(threshold) });
            toast.success(t("settings_saved"));
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <PageLoading />;

    const API_BASE = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

    return (
        <div className="space-y-6" data-testid="settings-page">
            <header>
                <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">{t("settings_title")}</h1>
                <p className="text-sm text-muted-foreground mt-1">{t("settings_subtitle")}</p>
            </header>

            {/* Language */}
            <Card className="card-paper shadow-none p-6 max-w-xl">
                <div className="flex items-center gap-2 mb-3">
                    <Translate size={18} color="#083D1E" weight="fill" />
                    <h2 className="font-display text-xl font-semibold">{t("language")}</h2>
                </div>
                <p className="text-sm text-muted-foreground mb-4">{t("language_desc")}</p>
                <div className="flex gap-2" data-testid="settings-lang-switcher">
                    {[
                        { code: "es", label: "Español" },
                        { code: "en", label: "English" },
                    ].map((l) => (
                        <button
                            key={l.code}
                            onClick={() => setLang(l.code)}
                            className={`px-4 py-2 rounded-md border text-sm font-medium transition-colors ${lang === l.code ? "bg-[#083D1E] text-white border-[#083D1E]" : "bg-white text-[#083D1E] border-[#DDE6D3] hover:bg-[#EAF3DA]"}`}
                            data-testid={`settings-lang-${l.code}`}
                        >
                            {l.label}
                        </button>
                    ))}
                </div>
            </Card>

            {/* Threshold */}
            <Card className="card-paper shadow-none p-6 max-w-xl">
                <div className="flex items-center gap-2 mb-3">
                    <Warning size={18} color="#4A6E00" weight="fill" />
                    <h2 className="font-display text-xl font-semibold">{t("alert_threshold")}</h2>
                </div>
                <p className="text-sm text-muted-foreground mb-4">{t("alert_threshold_desc")}</p>
                <form onSubmit={save} className="space-y-4">
                    <div className="space-y-1.5">
                        <Label>{t("max_fcr")}</Label>
                        <Input
                            type="number"
                            step="0.1"
                            min="0.1"
                            value={threshold}
                            onChange={(e) => setThreshold(e.target.value)}
                            required
                            data-testid="threshold-input"
                        />
                    </div>
                    <Button type="submit" disabled={saving} className="bg-[#083D1E] hover:bg-[#052A14] text-white" data-testid="save-threshold-btn">
                        <FloppyDisk size={16} className="mr-2" />
                        {saving ? t("saving") : t("save")}
                    </Button>
                </form>
            </Card>

            <Card className="card-paper shadow-none p-6 max-w-3xl">
                <h2 className="font-display text-xl font-semibold mb-2">{t("feeder_endpoint")}</h2>
                <p className="text-sm text-muted-foreground mb-4">{t("feeder_endpoint_desc")}</p>
                <div className="space-y-3 text-sm">
                    <div className="bg-[#101713] text-[#EAF3DA] rounded-md p-4 font-mono text-xs overflow-x-auto">
                        <div className="text-[#4A6E00]">POST</div>
                        <div>{API_BASE}/feed/events/ingest</div>
                    </div>
                    <div className="bg-[#101713] text-[#EAF3DA] rounded-md p-4 font-mono text-xs overflow-x-auto">
                        <pre>{`{
  "rfid": "A001",
  "feed_kg": 2.5,
  "timestamp": "2026-02-10T08:30:00Z"
}`}</pre>
                    </div>
                    <p className="text-sm text-muted-foreground mt-4">{t("parlour_endpoint_desc")}</p>
                    <div className="bg-[#101713] text-[#EAF3DA] rounded-md p-4 font-mono text-xs overflow-x-auto">
                        <div className="text-[#4A6E00]">POST</div>
                        <div>{API_BASE}/milk/ingest</div>
                    </div>
                    <div className="bg-[#101713] text-[#EAF3DA] rounded-md p-4 font-mono text-xs overflow-x-auto">
                        <pre>{`{
  "rfid": "M001",
  "liters": 22.5,
  "timestamp": "2026-02-10T06:00:00Z"
}`}</pre>
                    </div>
                </div>
            </Card>
        </div>
    );
}
